import React from "react";
import { Save, Add, ContentCopy, PriorityHigh } from "@mui/icons-material";
import { Button, CircularProgress, List, ListItemButton } from "@mui/material";
import SingleFontStyleEditor from "src/components/pages/projectAdmin/SingleFontStyleEditor";
import { FontStyleOverride, ProjectFontDefinition, UiFontStyleOverride } from "src/components/types";

type FontEditorProps = {
  saveFonts: () => void;
  fontProcessing: boolean;
  fontSaveStatus: string;
  addFontOverride: () => void;
  addFontOverrideFromCopy: () => void;
  fontOverrideForCopy: FontStyleOverride | null;
  fontOverrides: UiFontStyleOverride[];
  fontEditorListItemClick: (event: React.MouseEvent<HTMLDivElement>, id: string) => void;
  removeFontOverride: (id: string) => void;
  updateFontOverride: (override: UiFontStyleOverride) => void;
  copyFontOverride: (id: string) => void;
  projectFonts: ProjectFontDefinition[];
  layerNames: string[];
  cardParameters: string[];
};

export default function FontEditor(props: FontEditorProps) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
        <Button variant="contained" size="small" color="success" onClick={props.saveFonts}>
          <Save />
        </Button>
        {props.fontProcessing && <CircularProgress size={20} style={{ marginLeft: "10px", alignSelf: "center" }} />}
        <span style={{ marginLeft: "10px", alignSelf: "center" }}>{props.fontSaveStatus}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
        <div style={{ display: "flex", flexDirection: "column", marginRight: "20px", minWidth: "200px" }}>
          <div style={{ display: "flex", flexDirection: "row", gap: "5px" }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                props.addFontOverride();
              }}
              sx={{ flex: 1 }}
            >
              <Add />
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={!props.fontOverrideForCopy}
              onClick={props.addFontOverrideFromCopy}
              sx={{ flex: 1 }}
            >
              <Add />
              <ContentCopy />
            </Button>
          </div>
          <List>
            {props.fontOverrides
              .sort((a, b) => a.layerName.localeCompare(b.layerName) || a.conditionOrder - b.conditionOrder)
              .map((override) => (
                <ListItemButton
                  key={override.id}
                  onClick={(event) => props.fontEditorListItemClick(event, override.id)}
                  selected={override.selected}
                  sx={{
                    border: 1.2,
                    borderRadius: 1,
                    mb: 0.2,
                    "&.Mui-selected": { backgroundColor: "#0080ff69" },
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
                    <span>
                      {override.layerName ? override.layerName : "(no layer)"}
                      {override.subtextId ? ` (${override.subtextId})` : ""}
                      {override.conditionParameter && override.conditionValue ? ` (C${override.conditionOrder})` : ""}
                    </span>
                    <div style={{ flex: 1 }} />
                    {override.unsaved && <PriorityHigh color="error" />}
                  </div>
                </ListItemButton>
              ))}
          </List>
        </div>
        <div className="single-editors-container">
          {props.fontOverrides
            .filter((override) => override.selected)
            .map((override) => (
              <SingleFontStyleEditor
                key={override.id}
                override={override}
                projectFonts={props.projectFonts}
                onDelete={props.removeFontOverride}
                layerNames={props.layerNames}
                onChange={props.updateFontOverride}
                onCopy={props.copyFontOverride}
                cardParameters={props.cardParameters}
              />
            ))}
        </div>
      </div>
    </div>
  );
}
